import { BaseInitClip } from '@goldcast/player-config/src/classes/BaseInitClip';
import { ClipAvailabilityStatus } from '@goldcast/player-config/src/types/clip-availability';
import { Word } from '@goldcast/player-config/src/types/transcript-types';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { BrandKitStore, PlatformBrandKitStore } from '@goldcast/player-config/src/types/brand-kit-types';
import { updateClip } from '@goldcast/player-config/src/retrievers/updateClip';
import { getAllCompositeSegments } from '@goldcast/player-config/src/utils/clipUtils';
import { updateClipAvailabilityStatus } from './clipAvailabilityStatus';
import { fullTranscriptStore } from './fullTranscriptStore';
import { allClips, currentClip, logMigrationEvent } from './clip';
import { brandKit, clipsBrandKit, platformBrandKitStore } from './brandKit';
import { initHistory } from './clipHistory';
import { addPendingUpdate } from './pendingEditsStore';
import appLogger from '@/services/logger';
import { infiniteSleep } from '@/libs/utils';
import { getVideoDurationFromSrc } from '@/libs/getVideoDuration';
import { showErrorToast, showInfoToast } from '@/libs/toast/toast';
import { zoomLevelStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/zoomLevel';
import {
  updateCaptions as updateSegmentCaptions,
  updateGlobalCaptions
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/captionUtils';
import { updateCaptions as updateClipCaptions } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/captionUtils';
import { addPauseEntriesToParagraphs, buildPauseDeletesMap } from '@/components/molecules/Transcript/pauseUtils';
import { TranscriptParagraph } from '@/components/molecules/Transcript/types';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';

/**
 * Reels whose `initializeClip` has finished in this session.
 *
 * `ReelTimelineReady` is a one-shot event, so a listener that subscribes after it fires never
 * hears it. This lets a late AI-edits apply ask "has init already finished?" instead of waiting
 * forever, and separates the two cases that a missing-captions check alone cannot: init has not
 * got there yet (wait) versus init finished and derivation failed (skip the edits).
 */
const reelTimelineReadyIds = new Set<string>();

export function isReelTimelineReady(clipId: string): boolean {
  return reelTimelineReadyIds.has(clipId);
}

export class ContentLabInitClip extends BaseInitClip {
  protected async onAvailabilityStatus(status: ClipAvailabilityStatus) {
    updateClipAvailabilityStatus(this.props.clipId, status);
    if (status !== ClipAvailabilityStatus.ALL_OK) {
      await infiniteSleep();
    }
  }
  protected async fetchVideoDuration(url: string): Promise<number> {
    return getVideoDurationFromSrc(url);
  }
  protected getAuthHeader(): string {
    return `Bearer ${window.accessToken}`;
  }
  protected onSpeakerAnalsysisFetchFail(error) {
    appLogger.error('Error fetching speakers analysis', error);
  }
  protected onErrorToast(content: string): void {
    showErrorToast(content);
  }
  protected onFullTranscript(transcript: Word[]): void {
    fullTranscriptStore.update(() => transcript);
  }
  protected logEvent(clip: Clip, name: string): void {
    logMigrationEvent(clip, name);
  }
  protected infoToast(content: string): void {
    showInfoToast(content);
  }
  protected getBrandkitValues(): {
    clipBrandKitStore: BrandKitStore;
    brandKitStore: BrandKitStore;
    platformBrandKit: PlatformBrandKitStore;
  } {
    return {
      clipBrandKitStore: clipsBrandKit.getSnapshot()[this.props.clipId],
      brandKitStore: brandKit.getSnapshot(),
      platformBrandKit: platformBrandKitStore.getSnapshot()
    };
  }
  protected getZoomLevel(): number {
    return zoomLevelStore.getSnapshot().zoomLevel;
  }
  protected getPreviousClip(): Clip {
    return currentClip.getSnapshot()[this.props.clipId];
  }
  protected updateCurrentClipStore(clip: Clip): void {
    currentClip.update(data => {
      return {
        ...data,
        [clip.id]: clip
      };
    });
  }
  protected initializeHistory(clip: Clip): void {
    initHistory(clip.id, clip.asset_metadata);
  }
  protected updateAllClipStore(clip: Clip): void {
    allClips.update(allClipsData => allClipsData.map(currClip => (currClip.id === clip.id ? clip : currClip)));
  }
  protected async persistClip({ clip, version }: { clip: Clip; version: number }): Promise<void> {
    const updatePromise = updateClip(clip);
    addPendingUpdate({ clipId: this.props.clipId, updatePromise, version });
    updatePromise.then(({ updated_at }) => {
      currentClip.update(data => {
        return {
          ...data,
          [this.props.clipId]: { ...data[this.props.clipId], updated_at }
        };
      });
    });
  }

  protected onReelTimelineReady(clipId: string): void {
    reelTimelineReadyIds.add(clipId);
    EventBus.dispatch(CustomEvents.ReelTimelineReady, { clipId });
  }

  protected deriveReelCaptions(clip: Clip): boolean {
    // A single-window reel has no `segment_metadata`, so its captions go on the clip.
    const isSplitsReel = getAllCompositeSegments(clip).some(segment => segment.segment_metadata);
    const hadClipCaptions = !!clip.asset_metadata.captions?.length;
    try {
      if (isSplitsReel) {
        updateSegmentCaptions(clip);
      } else {
        updateClipCaptions(clip);
      }
    } catch (error) {
      window.logger?.error('Highlight reel: failed to derive captions', error);
      return false;
    }
    let appliedPauseRemoval = false;
    try {
      appliedPauseRemoval = this.applyDefaultReelPauseRemoval(clip);
      if (appliedPauseRemoval) {
        // `duration` becomes the timeline slot; stale, it outlives the media and the playhead overruns.
        if (isSplitsReel) {
          updateSegmentCaptions(clip);
        } else {
          updateClipCaptions(clip);
        }
      }
    } catch (error) {
      window.logger?.error('Highlight reel: failed to apply default pause removal', error);
    }
    if (isSplitsReel) {
      try {
        // Roll up to the clip, as `updateWithSplits` does; otherwise clip-level derivations span
        // the whole envelope. Last, as it offsets by the `duration` corrected above.
        if (
          !clip.asset_metadata.selected_lang ||
          clip.asset_metadata.original_lang === clip.asset_metadata.selected_lang
        ) {
          // Skip when translated: this array is the source segment captions derive from.
          updateGlobalCaptions(clip);
        }
      } catch (error) {
        window.logger?.error('Highlight reel: failed to roll up clip captions', error);
      }
    }
    // Deriving a splits reel's segment captions is itself a change. A single-window reel usually
    // arrives with captions, so !hadClipCaptions stops a transcript-less load persisting on a loop.
    const hasClipCaptionsNow = !!clip.asset_metadata.captions?.length;
    return isSplitsReel || appliedPauseRemoval || (!hadClipCaptions && hasClipCaptionsNow);
  }

  /**
   * Remove pauses by default on a freshly-seeded reel. For each composite segment we detect pauses
   * from its captions (source-time word timings) and merge the resulting PAUSE_OPTIMIZATION deletes
   * into `segment_metadata.deletes` — alongside the structural head/tail trims — before config-gen
   * builds playRanges. "Trim excess only": `getRecommendedPauseDuration` keeps a natural residual
   * gap and deletes only the surplus (min detected gap 0.4s). Runs unconditionally for reels; these
   * are ordinary PAUSE_OPTIMIZATION deletes, so the existing Restore/Restore-all UX undoes them.
   */
  /** Returns whether any pause delete was actually added. */
  private applyDefaultReelPauseRemoval(clip: Clip): boolean {
    const compositeSegments = getAllCompositeSegments(clip);
    const segmentsWithMetadata = compositeSegments.filter(segment => segment.segment_metadata);

    // Single-window reel: the non-splits path leaves the composite without `segment_metadata`,
    // so its captions and deletes live on the clip itself.
    if (!segmentsWithMetadata.length) {
      const deletes = this.computePauseDeletes(
        compositeSegments[0]?.id ?? clip.id,
        clip.asset_metadata.start,
        clip.asset_metadata.captions
      );
      if (!Object.keys(deletes).length) {
        return false;
      }
      clip.asset_metadata.deletes = { ...(clip.asset_metadata.deletes ?? {}), ...deletes };
      return true;
    }

    let applied = false;
    for (const segment of segmentsWithMetadata) {
      const meta = segment.segment_metadata!;
      const deletes = this.computePauseDeletes(segment.id, meta.start, meta.captions);
      if (Object.keys(deletes).length) {
        meta.deletes = { ...(meta.deletes ?? {}), ...deletes };
        applied = true;
      }
    }
    return applied;
  }

  /** PAUSE_OPTIMIZATION deletes for one transcript window; `{}` when there is nothing to trim. */
  private computePauseDeletes(segmentId: string, segmentStartTime: number, words: Word[] | undefined) {
    if (!words || words.length < 2) {
      return {};
    }
    const paragraph: TranscriptParagraph = { segmentId, segmentStartTime, words };
    const [paragraphWithPauses] = addPauseEntriesToParagraphs([paragraph]);
    const entries = paragraphWithPauses.pauseEntries ?? [];
    if (!entries.length) {
      return {};
    }
    return buildPauseDeletesMap(entries);
  }
}
